export function hasExplicitTimezone(value) {
  return typeof value === 'string' && /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
}

export function parseDateParts(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second] = match;
  return {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
    second: Number(second || '0'),
  };
}

export function normalizeTimezoneInput(value) {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    if (trimmed.startsWith('"')) {
      try {
        return normalizeTimezoneInput(JSON.parse(trimmed));
      } catch (error) {
        return null;
      }
    }

    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        return normalizeTimezoneInput(JSON.parse(trimmed));
      } catch (error) {
        const extracted = extractTimezoneFromSerializedString(trimmed);
        return extracted || trimmed;
      }
    }

    return trimmed;
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const candidate = normalizeTimezoneInput(item);
      if (candidate) {
        return candidate;
      }
    }

    return null;
  }

  if (typeof value === 'object') {
    for (const key of ['zoneName', 'name', 'timezone', 'tzName']) {
      const candidate = normalizeTimezoneInput(value[key]);
      if (candidate) {
        return candidate;
      }
    }
  }

  return null;
}

function extractTimezoneFromSerializedString(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value
    .replace(/\\\//g, '/')
    .replace(/'/g, '"');

  const matches = normalized.match(/[A-Za-z_]+\/[A-Za-z_\-+]+(?:\/[A-Za-z_\-+]+)*/g);
  if (!matches || matches.length === 0) {
    return null;
  }

  const uniqueMatches = [...new Set(matches)];
  const preferred = uniqueMatches.find((timezone) => /(?:^|\/)Santiago$/i.test(timezone))
    || uniqueMatches.find((timezone) => /(?:^|\/)Rome$/i.test(timezone))
    || uniqueMatches.find((timezone) => /(?:^|\/)Berlin$/i.test(timezone))
    || uniqueMatches[0];

  return preferred || null;
}

export function getTimeZoneParts(timestamp, timeZone) {
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });

    const formattedParts = formatter.formatToParts(new Date(timestamp));
    const values = {};

    formattedParts.forEach((part) => {
      if (part.type !== 'literal') {
        values[part.type] = Number(part.value);
      }
    });

    if (!Number.isFinite(values.year) || !Number.isFinite(values.month) || !Number.isFinite(values.day)) {
      return null;
    }

    return {
      year: values.year,
      month: values.month,
      day: values.day,
      hour: Number.isFinite(values.hour) ? values.hour : 0,
      minute: Number.isFinite(values.minute) ? values.minute : 0,
      second: Number.isFinite(values.second) ? values.second : 0,
    };
  } catch (error) {
    return null;
  }
}

export function zonedDateTimeToUtcMs(parts, timeZone) {
  const desiredUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second,
  );

  let guess = desiredUtc;

  for (let index = 0; index < 4; index += 1) {
    const zonedParts = getTimeZoneParts(guess, timeZone);
    if (!zonedParts) {
      return null;
    }

    const currentUtc = Date.UTC(
      zonedParts.year,
      zonedParts.month - 1,
      zonedParts.day,
      zonedParts.hour,
      zonedParts.minute,
      zonedParts.second,
    );

    const delta = desiredUtc - currentUtc;
    guess += delta;

    if (delta === 0) {
      return guess;
    }
  }

  return guess;
}

export function parseCompetitionDate(rawDate, timezoneInput) {
  if (!rawDate || typeof rawDate !== 'string') {
    return null;
  }

  const normalized = rawDate.trim();
  if (!normalized) {
    return null;
  }

  if (hasExplicitTimezone(normalized)) {
    const explicitDate = new Date(normalized.replace(' ', 'T'));
    return Number.isNaN(explicitDate.getTime()) ? null : explicitDate;
  }

  const parsedParts = parseDateParts(normalized);
  const resolvedTimeZone = normalizeTimezoneInput(timezoneInput);
  if (parsedParts && resolvedTimeZone) {
    const utcMs = zonedDateTimeToUtcMs(parsedParts, resolvedTimeZone);
    if (utcMs !== null) {
      return new Date(utcMs);
    }
  }

  const fallbackDate = new Date(normalized.replace(' ', 'T'));
  return Number.isNaN(fallbackDate.getTime()) ? null : fallbackDate;
}

export function getCompetitionDateParts(date, timezoneInput) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const resolvedTimeZone = normalizeTimezoneInput(timezoneInput);
  if (resolvedTimeZone) {
    return getTimeZoneParts(date.getTime(), resolvedTimeZone);
  }

  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  };
}

export function formatCompetitionTime(date, timezoneInput, locale = 'es-ES') {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const resolvedTimeZone = normalizeTimezoneInput(timezoneInput);
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTimeZone || undefined,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  } catch (error) {
    return new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(date);
  }
}

export function resolveCompetitionTimezone(source) {
  const candidates = [
    source?.competitions_info?.country_timezone,
    source?.competitions_info?.country_timezones,
    source?.competitions_info?.country?.timezones,
    source?.country_timezone,
    source?.country_timezones,
    source?.country?.timezones,
    source?.timezones,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeTimezoneInput(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return null;
}